import { useEffect, useRef, useState, useCallback } from 'react';
import { useAgentStore } from '@/stores/agentStore';
import { useTaskStore } from '@/stores/taskStore';
import { eventBus, CanvasEvents } from '@/utils/eventBus';
import { config } from '@/config';

interface CollaborationEvent {
  type: string;
  collaboration_id?: string;
  call_id?: string;
  status?: string;
  progress?: number;
  message?: string;
  result_summary?: string;
  cost?: number;
  error_code?: string;
  error_message?: string;
  level?: number;
  question?: string;
  options?: string[];
  timestamp: string;
  task_id?: string;
  data?: {
    status?: string;
    result?: any;
    error?: string;
    [key: string]: any;
  };
}

export function useCollaborationWebSocket({ sessionId, autoConnect = true, onClarificationRequest }: { sessionId: string; autoConnect?: boolean; onClarificationRequest?: (data: { callId: string; question: string; options: string[] }) => void }) {
  const wsRef = useRef<WebSocket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [lastEvent, setLastEvent] = useState<CollaborationEvent | null>(null);
  const reconnectAttempts = useRef(0);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const maxReconnectAttempts = 5;
  const isUnmountedRef = useRef(false);

  const setDegradationStatus = useAgentStore(state => state.setDegradationStatus);

  const setDegradationStatusRef = useRef(setDegradationStatus);
  const onClarificationRequestRef = useRef(onClarificationRequest);

  setDegradationStatusRef.current = setDegradationStatus;
  onClarificationRequestRef.current = onClarificationRequest;

  const connect = useCallback(() => {
    if (!sessionId || isUnmountedRef.current) return;

    if (wsRef.current) {
      if (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING) {
        return;
      }
    }

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsHost = config.apiBase.replace(/^https?:\/\//, '');
    const wsUrl = `${protocol}//${wsHost}/api/agent/ws/collaboration?session_id=${sessionId}`;

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      setIsConnected(true);
      reconnectAttempts.current = 0;
    };

    ws.onmessage = (event) => {
      try {
        const data: CollaborationEvent = JSON.parse(event.data);
        setLastEvent(data);

        switch (data.type) {
          case 'dispatcher_degraded':
            setDegradationStatusRef.current?.(true, data.level, data.message);
            break;

          case 'clarification_request':
            if (onClarificationRequestRef.current && data.call_id && data.question) {
              onClarificationRequestRef.current({
                callId: data.call_id,
                question: data.question,
                options: data.options || [],
              });
            }
            break;

          case 'task_update':
            // 处理后端 _notify_agent_sessions 发送的任务更新
            // 统一通过 AGENT_TASK_COMPLETED 事件更新面板和画布，避免与 useTaskUpdates 双重更新
            if (data.task_id && data.data) {
              const taskStatus = data.data.status;
              const taskResult = data.data.result;
              const taskError = data.data.error;
              const isFinal = taskStatus === 'success' || taskStatus === 'failed';

              if (isFinal) {
                const newStatus = taskStatus === 'success' ? 'SUCCESS' : 'FAILED';
                const taskStore = useTaskStore.getState();
                const task = taskStore.pendingTasks.get(data.task_id);

                if (task && task.nodeId) {
                  // taskStore 中有记录：通过 taskStore 更新状态并触发事件
                  taskStore.updateTaskStatus(data.task_id, newStatus);
                  if (newStatus === 'SUCCESS') {
                    eventBus.emit(CanvasEvents.AGENT_TASK_COMPLETED, {
                      taskId: data.task_id,
                      nodeId: task.nodeId,
                      status: 'SUCCESS',
                      updates: taskResult || {},
                    });
                  } else {
                    eventBus.emit(CanvasEvents.AGENT_TASK_STATUS_CHANGED, {
                      taskId: data.task_id,
                      nodeId: task.nodeId,
                      status: 'FAILED',
                    });
                  }
                  taskStore.removeTask(data.task_id);
                } else {
                  // taskStore 中没有记录（可能通过其他路径添加的面板消息）：
                  // 直接更新面板消息，因为没有 taskStore 记录来触发事件
                  useAgentStore.getState().setMessages((prev) =>
                    prev.map((m) => {
                      const matchByMediaData = m.mediaData && m.mediaData.taskId === data.task_id;
                      const matchByMetadata = m.metadata?.pendingTaskId === data.task_id;
                      if (matchByMediaData || matchByMetadata) {
                        return {
                          ...m,
                          mediaData: m.mediaData
                            ? {
                                ...m.mediaData,
                                status: newStatus,
                                results: taskResult?.results || m.mediaData?.results || [],
                                ...(taskResult?.videoMetadata ? { videoMetadata: taskResult.videoMetadata } : {}),
                                ...(taskError ? { error: taskError } : {}),
                              }
                            : undefined,
                          text: newStatus === 'FAILED' ? `生成失败：${taskError || '未知错误'}` : '',
                          metadata: {
                            ...m.metadata,
                            taskStatus: newStatus === 'SUCCESS' ? 'COMPLETED' : 'FAILED',
                          },
                        };
                      }
                      return m;
                    })
                  );
                }
              }
            }
            break;
        }
      } catch (e) {
        console.error('Failed to parse websocket message:', e);
      }
    };

    ws.onclose = () => {
      setIsConnected(false);
      if (wsRef.current === ws) {
        wsRef.current = null;
      }

      if (isUnmountedRef.current) return;

      if (reconnectAttempts.current < maxReconnectAttempts) {
        reconnectAttempts.current += 1;
        reconnectTimerRef.current = setTimeout(connect, 1000 * reconnectAttempts.current);
      }
    };

    ws.onerror = () => {};
  }, [sessionId]);

  useEffect(() => {
    isUnmountedRef.current = false;

    if (autoConnect && sessionId) {
      connect();
    }

    return () => {
      isUnmountedRef.current = true;
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      reconnectAttempts.current = maxReconnectAttempts;
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [connect, autoConnect, sessionId]);

  const sendMessage = useCallback((message: object) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
    }
  }, []);

  const sendClarificationReply = useCallback((callId: string, reply: string) => {
    sendMessage({ type: 'clarification_reply', call_id: callId, reply });
  }, [sendMessage]);

  return {
    isConnected,
    lastEvent,
    sendMessage,
    sendClarificationReply,
  };
}
